import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import { useAuthContext } from "../../hooks/useAuthContext";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import { database, timestamp } from "../../firebase/config";
import "../gestioneDocumenti/GestioneDocumenti.css";

const selectModalStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const parseNumberIT = (value) => {
  if (value == null || value === "") return NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  const raw = String(value).trim();
  if (!raw) return NaN;
  let normalized = raw.replace(/\s/g, "");
  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
};

const parseImporto = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return NaN;

  let normalized = raw.replace(/\s/g, "");
  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
};

const formatCurrency = (value) => {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });
};

const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate();
    } catch (_) {
      return null;
    }
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toIsoStringSafe = (value) => {
  const d = toJsDate(value);
  return d ? d.toISOString() : "";
};

const escapeCsvValue = (value) => {
  if (value == null) return "";
  const s = String(value);
  if (/[",\n\r;]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const getMonthKey = (date) => {
  if (!date) return "";
  const d = toJsDate(date);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

export default function Ricavi() {
  const { user } = useAuthContext();
  const admin = useIsAdmin(user);

  const { documents: projects } = useCollection(
    "projects",
    null,
    null,
    null,
    false,
  );
  const { documents: consuntivi } = useCollection(
    "consuntivi",
    null,
    null,
    null,
    false,
  );
  const { documents, error } = useCollection("ricavi", null, [
    "createdAt",
    "desc",
  ]);
  const { addDocument, deleteDocument, updateDocument } =
    useFirestore("ricavi");

  const [searchTerm, setSearchTerm] = useState("");
  const [filterMeseCompetenza, setFilterMeseCompetenza] = useState("");
  const [onlyNoInvoice, setOnlyNoInvoice] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showMonthDetailModal, setShowMonthDetailModal] = useState(false);
  const [monthDetailMonth, setMonthDetailMonth] = useState("");
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadMonth, setDownloadMonth] = useState("");
  const [showFattureTable, setShowFattureTable] = useState(true);
  const [duplicateSourceId, setDuplicateSourceId] = useState("");
  const [selectedInvoiceNr, setSelectedInvoiceNr] = useState("");
  const [isSavingAdd, setIsSavingAdd] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isSavingImport, setIsSavingImport] = useState(false);
  const [isAssigningInvoice, setIsAssigningInvoice] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [bulkNrFattura, setBulkNrFattura] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [formState, setFormState] = useState({
    cliente: "",
    nrFattura: "",
    numero_documento: "",
    luogoIntervento: "",
    meseCompetenza: "",
    settore: "",
    descrizione: "",
    unitaMisura: "",
    importo: "",
  });
  const [formError, setFormError] = useState("");
  const [editError, setEditError] = useState("");
  const [editingRowId, setEditingRowId] = useState("");
  const [editState, setEditState] = useState({
    cliente: "",
    nrFattura: "",
    numero_documento: "",
    luogoIntervento: "",
    meseCompetenza: "",
    settore: "",
    descrizione: "",
    unitaMisura: "",
    importo: "",
  });
  const [importError, setImportError] = useState("");
  const [consuntiviMonthFilter, setConsuntiviMonthFilter] = useState(
    getMonthKey(new Date()),
  );
  const [selectedConsuntivoId, setSelectedConsuntivoId] = useState("");
  const [importRows, setImportRows] = useState([]);
  const [importIsLoading, setImportIsLoading] = useState(false);
  const [useUniformSector, setUseUniformSector] = useState(true);
  const [uniformSector, setUniformSector] = useState("");
  const [importMeseCompetenza, setImportMeseCompetenza] = useState("");

  const selectAllRef = useRef(null);

  const projectOptions = useMemo(() => {
    const rows = Array.isArray(projects) ? projects : [];
    return rows
      .filter((p) => (p?.codice ?? "").toString().trim())
      .map((p) => {
        const codice = (p.codice ?? "").toString().trim();
        const nome =
          (p.nome ?? "").toString().trim() ||
          (p.dettagli ?? "").toString().trim() ||
          "Senza nome";
        return { value: codice, label: `${codice} - ${nome}` };
      })
      .sort((a, b) => a.value.localeCompare(b.value, "it"));
  }, [projects]);

  const projectByCodice = useMemo(() => {
    const map = new Map();
    (Array.isArray(projects) ? projects : []).forEach((p) => {
      const codice = (p?.codice ?? "").toString().trim();
      if (!codice) return;
      map.set(codice, p);
    });
    return map;
  }, [projects]);

  const projectById = useMemo(() => {
    const map = new Map();
    (Array.isArray(projects) ? projects : []).forEach((p) => {
      if (!p?.id) return;
      map.set(String(p.id), p);
    });
    return map;
  }, [projects]);

  useEffect(() => {
    if (
      !showAddModal &&
      !showImportModal &&
      !showEditModal &&
      !showInvoiceModal &&
      !showMonthDetailModal &&
      !showDownloadModal
    )
      return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [
    showAddModal,
    showEditModal,
    showImportModal,
    showInvoiceModal,
    showMonthDetailModal,
    showDownloadModal,
  ]);

  useEffect(() => {
    if (
      !showAddModal &&
      !showImportModal &&
      !showEditModal &&
      !showInvoiceModal &&
      !showMonthDetailModal &&
      !showDownloadModal
    )
      return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showImportModal) {
          setShowImportModal(false);
          setImportError("");
        } else if (showEditModal) {
          setShowEditModal(false);
          setEditError("");
        } else if (showInvoiceModal) {
          setShowInvoiceModal(false);
          setSelectedInvoiceNr("");
        } else if (showMonthDetailModal) {
          setShowMonthDetailModal(false);
          setMonthDetailMonth("");
        } else if (showDownloadModal) {
          setShowDownloadModal(false);
          setDownloadMonth("");
        } else {
          setShowAddModal(false);
          setFormError("");
          setDuplicateSourceId("");
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    showAddModal,
    showEditModal,
    showImportModal,
    showInvoiceModal,
    showMonthDetailModal,
    showDownloadModal,
  ]);

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const rows = Array.isArray(documents) ? documents : [];
    const filteredByMonth = rows.filter((r) => {
      if (!filterMeseCompetenza) return true;
      const month =
        (r?.meseCompetenza ?? "").toString().trim() ||
        getMonthKey(r?.createdAt);
      return month === filterMeseCompetenza;
    });

    const filteredByInvoice = filteredByMonth.filter((r) => {
      if (!onlyNoInvoice) return true;
      const nr = (r?.nrFattura ?? "").toString().trim();
      return !nr;
    });

    if (!term) return filteredByInvoice;

    return filteredByInvoice.filter((r) => {
      const cliente = (r?.cliente ?? "").toString().toLowerCase();
      const nrFattura = (r?.nrFattura ?? "").toString().toLowerCase();
      const settore = (r?.settore ?? "").toString().toLowerCase();
      const descrizione = (r?.descrizione ?? "").toString().toLowerCase();
      const um = (r?.unitaMisura ?? "").toString().toLowerCase();
      const luogo = (r?.luogoIntervento ?? "").toString().toLowerCase();
      return (
        cliente.includes(term) ||
        nrFattura.includes(term) ||
        settore.includes(term) ||
        descrizione.includes(term) ||
        um.includes(term) ||
        luogo.includes(term)
      );
    });
  }, [documents, filterMeseCompetenza, onlyNoInvoice, searchTerm]);

  const filteredTotalImporto = useMemo(() => {
    return (Array.isArray(filteredRows) ? filteredRows : []).reduce(
      (acc, r) => {
        const n =
          typeof r?.importo === "number"
            ? r.importo
            : parseNumberIT(r?.importo);
        return acc + (Number.isFinite(n) ? n : 0);
      },
      0,
    );
  }, [filteredRows]);

  const selectedRowForDuplication = useMemo(() => {
    if (!selectedRowIds || selectedRowIds.length !== 1) return null;
    const id = selectedRowIds[0];
    return (
      (Array.isArray(filteredRows) ? filteredRows : []).find(
        (r) => r.id === id,
      ) || null
    );
  }, [filteredRows, selectedRowIds]);

  const filteredRowIdSet = useMemo(
    () => new Set((filteredRows || []).map((r) => r.id)),
    [filteredRows],
  );

  useEffect(() => {
    setSelectedRowIds((prev) => prev.filter((id) => filteredRowIdSet.has(id)));
  }, [filteredRowIdSet]);

  const allVisibleSelected = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return false;
    return filteredRows.every((r) => selectedRowIds.includes(r.id));
  }, [filteredRows, selectedRowIds]);

  const someVisibleSelected = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return false;
    return filteredRows.some((r) => selectedRowIds.includes(r.id));
  }, [filteredRows, selectedRowIds]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      someVisibleSelected && !allVisibleSelected;
  }, [allVisibleSelected, someVisibleSelected]);

  const fattureRaggruppate = useMemo(() => {
    const groups = new Map();
    (Array.isArray(filteredRows) ? filteredRows : []).forEach((r) => {
      const key = (r?.nrFattura ?? "").toString().trim();
      if (!key) return;
      const prev = groups.get(key) || {
        nrFattura: key,
        cliente: "",
        totale: 0,
        count: 0,
      };
      const cliente = (r?.cliente ?? "").toString().trim();
      if (!prev.cliente && cliente) prev.cliente = cliente;
      const imp =
        typeof r?.importo === "number" ? r.importo : Number(r?.importo);
      prev.totale += Number.isFinite(imp) ? imp : 0;
      prev.count += 1;
      groups.set(key, prev);
    });
    return Array.from(groups.values()).sort((a, b) =>
      a.nrFattura.localeCompare(b.nrFattura, "it"),
    );
  }, [filteredRows]);

  const macroSettoreTotals = useMemo(() => {
    const month = String(monthDetailMonth || "").trim();
    if (!month) return [];
    const rows = Array.isArray(documents) ? documents : [];
    const filtered = rows.filter((r) => {
      const rowMonth =
        (r?.meseCompetenza ?? "").toString().trim() ||
        getMonthKey(r?.createdAt);
      if (rowMonth !== month) return false;
      if (!onlyNoInvoice) return true;
      return !(r?.nrFattura ?? "").toString().trim();
    });

    const groups = new Map();
    filtered.forEach((r) => {
      const settore = (r?.settore ?? "").toString().trim();
      const proj = projectById.get(settore) || projectByCodice.get(settore);
      const macro =
        (proj?.categoria ?? "").toString().trim() || "Non classificato";
      const prev = groups.get(macro) || { macro, totale: 0, count: 0 };
      const imp =
        typeof r?.importo === "number" ? r.importo : Number(r?.importo);
      prev.totale += Number.isFinite(imp) ? imp : 0;
      prev.count += 1;
      groups.set(macro, prev);
    });

    return Array.from(groups.values()).sort((a, b) => b.totale - a.totale);
  }, [
    documents,
    monthDetailMonth,
    onlyNoInvoice,
    projectByCodice,
    projectById,
  ]);

  const macroSettoreGrandTotal = useMemo(() => {
    return macroSettoreTotals.reduce((acc, r) => acc + (r?.totale || 0), 0);
  }, [macroSettoreTotals]);

  const downloadMonthRows = useMemo(() => {
    const month = String(downloadMonth || "").trim();
    if (!month) return [];
    const rows = Array.isArray(documents) ? documents : [];
    return rows.filter((r) => {
      const rowMonth =
        (r?.meseCompetenza ?? "").toString().trim() ||
        getMonthKey(r?.createdAt);
      return rowMonth === month;
    });
  }, [documents, downloadMonth]);

  const handleDownloadCsv = () => {
    const hasSelection =
      Array.isArray(selectedRowIds) && selectedRowIds.length > 0;
    const month = String(downloadMonth || "").trim();
    if (!hasSelection && !month) return;
    const selectedSet = new Set(selectedRowIds || []);
    const rows = hasSelection
      ? (Array.isArray(filteredRows) ? filteredRows : []).filter((r) =>
          selectedSet.has(r.id),
        )
      : Array.isArray(downloadMonthRows)
        ? downloadMonthRows
        : [];

    const header = [
      "id",
      "meseCompetenza",
      "cliente",
      "nrFattura",
      "numero_documento",
      "luogoIntervento",
      "settore",
      "settoreLabel",
      "categoria",
      "descrizione",
      "unitaMisura",
      "importo",
      "createdAt",
    ];

    const lines = [header.join(";")];
    rows.forEach((r) => {
      const settoreRaw = (r?.settore ?? "").toString().trim();
      const proj =
        projectById.get(settoreRaw) || projectByCodice.get(settoreRaw);
      const settoreLabel = proj
        ? `${(proj?.codice ?? "").toString().trim()} - ${(
            proj?.nome ??
            proj?.name ??
            proj?.dettagli ??
            ""
          )
            .toString()
            .trim()}`
        : settoreRaw;
      const categoria = (proj?.categoria ?? "").toString().trim();
      const row = [
        r?.id ?? "",
        (r?.meseCompetenza ?? "").toString().trim() ||
          getMonthKey(r?.createdAt),
        (r?.cliente ?? "").toString(),
        (r?.nrFattura ?? "").toString(),
        (r?.numero_documento ?? "").toString(),
        (r?.luogoIntervento ?? "").toString(),
        settoreRaw,
        settoreLabel,
        categoria,
        (r?.descrizione ?? "").toString(),
        (r?.unitaMisura ?? "").toString(),
        typeof r?.importo === "number" ? r.importo : Number(r?.importo),
        toIsoStringSafe(r?.createdAt),
      ].map(escapeCsvValue);
      lines.push(row.join(";"));
    });

    const csv = `${lines.join("\n")}\n`;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    if (hasSelection) {
      a.download = month
        ? `ricavi_${month}_selezione.csv`
        : "ricavi_selezione.csv";
    } else {
      a.download = `ricavi_${month}.csv`;
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const consuntivoOptions = useMemo(() => {
    const rows = Array.isArray(consuntivi) ? consuntivi : [];
    return rows
      .filter((c) => {
        if (!consuntiviMonthFilter) return true;
        const d = toJsDate(c?.data_creazione);
        const key = getMonthKey(d);
        return key === consuntiviMonthFilter;
      })
      .slice()
      .sort((a, b) => {
        const da = toJsDate(a?.data_creazione);
        const db = toJsDate(b?.data_creazione);
        const ta = da ? da.getTime() : 0;
        const tb = db ? db.getTime() : 0;
        return tb - ta;
      })
      .map((c) => {
        const d = toJsDate(c?.data_creazione);
        const dateLabel = d ? d.toLocaleString("it-IT") : "Senza data";
        const cliente = (c?.cliente ?? "Sconosciuto").toString();
        const luogo = (c?.luogo_lavoro ?? "").toString();
        const tot = c?.totale_complessivo ?? "";
        const label = `${dateLabel} — ${cliente}${luogo ? ` — ${luogo}` : ""}${
          tot ? ` — € ${tot}` : ""
        }`;
        return { value: c.id, label };
      });
  }, [consuntivi, consuntiviMonthFilter]);

  const openNewModal = () => {
    setFormError("");
    setIsSavingAdd(false);
    setDuplicateSourceId("");
    setFormState({
      cliente: "",
      nrFattura: "",
      numero_documento: "",
      luogoIntervento: "",
      meseCompetenza: getMonthKey(new Date()),
      settore: "",
      descrizione: "",
      unitaMisura: "",
      importo: "",
    });
    setShowAddModal(true);
  };

  const openDuplicateModal = () => {
    const row = selectedRowForDuplication;
    if (!row?.id) {
      alert("Seleziona una sola riga per duplicare.");
      return;
    }
    setFormError("");
    setIsSavingAdd(false);
    setDuplicateSourceId(row.id);
    setFormState({
      cliente: (row?.cliente ?? "").toString(),
      nrFattura: (row?.nrFattura ?? "").toString(),
      numero_documento: (row?.numero_documento ?? "").toString(),
      luogoIntervento: (row?.luogoIntervento ?? "").toString(),
      meseCompetenza:
        (row?.meseCompetenza ?? "").toString().trim() ||
        getMonthKey(row?.createdAt) ||
        getMonthKey(new Date()),
      settore: (row?.settore ?? "").toString(),
      descrizione: (row?.descrizione ?? "").toString(),
      unitaMisura: (row?.unitaMisura ?? "").toString(),
      importo:
        row?.importo == null
          ? ""
          : typeof row.importo === "number"
            ? String(row.importo)
            : String(row.importo),
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setFormError("");
    setIsSavingAdd(false);
    setDuplicateSourceId("");
  };

  const openEditModal = (row) => {
    if (!row?.id) return;
    setEditError("");
    setIsSavingEdit(false);
    setEditingRowId(row.id);
    setEditState({
      cliente: (row?.cliente ?? "").toString(),
      nrFattura: (row?.nrFattura ?? "").toString(),
      numero_documento: (row?.numero_documento ?? "").toString(),
      luogoIntervento: (row?.luogoIntervento ?? "").toString(),
      meseCompetenza:
        (row?.meseCompetenza ?? "").toString().trim() ||
        getMonthKey(row?.createdAt) ||
        getMonthKey(new Date()),
      settore: (row?.settore ?? "").toString(),
      descrizione: (row?.descrizione ?? "").toString(),
      unitaMisura: (row?.unitaMisura ?? "").toString(),
      importo:
        row?.importo == null
          ? ""
          : typeof row.importo === "number"
            ? String(row.importo)
            : String(row.importo),
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditError("");
    setEditingRowId("");
    setIsSavingEdit(false);
  };

  const openImportModal = () => {
    setImportError("");
    setIsSavingImport(false);
    setConsuntiviMonthFilter(getMonthKey(new Date()));
    setSelectedConsuntivoId("");
    setImportRows([]);
    setUseUniformSector(true);
    setUniformSector("");
    setImportMeseCompetenza(getMonthKey(new Date()));
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportError("");
    setIsSavingImport(false);
  };

  const openInvoiceModal = (nrFattura) => {
    const nr = String(nrFattura ?? "").trim();
    if (!nr) return;
    setSelectedInvoiceNr(nr);
    setShowInvoiceModal(true);
  };

  const closeInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoiceNr("");
  };

  const invoiceDetailRows = useMemo(() => {
    const nr = String(selectedInvoiceNr ?? "").trim();
    if (!nr) return [];
    return (Array.isArray(filteredRows) ? filteredRows : []).filter(
      (r) => String(r?.nrFattura ?? "").trim() === nr,
    );
  }, [filteredRows, selectedInvoiceNr]);

  const invoiceDetailTotal = useMemo(() => {
    return invoiceDetailRows.reduce((acc, r) => {
      const imp =
        typeof r?.importo === "number" ? r.importo : Number(r?.importo);
      return acc + (Number.isFinite(imp) ? imp : 0);
    }, 0);
  }, [invoiceDetailRows]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setIsSavingAdd(true);

    const cliente = formState.cliente.trim();
    const nrFattura = String(formState.nrFattura || "").trim();
    const numeroDocumento = String(formState.numero_documento || "").trim();
    const luogoIntervento = String(formState.luogoIntervento || "").trim();
    const meseCompetenza = String(formState.meseCompetenza || "").trim();
    const settore = formState.settore.trim();
    const descrizione = formState.descrizione.trim();
    const unitaMisura = formState.unitaMisura.trim();
    const importoNumber = parseImporto(formState.importo);

    if (
      !cliente ||
      !meseCompetenza ||
      !settore ||
      !descrizione ||
      !unitaMisura
    ) {
      setFormError("Compila tutti i campi.");
      setIsSavingAdd(false);
      return;
    }

    if (!Number.isFinite(importoNumber)) {
      setFormError("Importo non valido.");
      setIsSavingAdd(false);
      return;
    }

    try {
      const created = await addDocument({
        cliente,
        nrFattura: nrFattura || "",
        numero_documento: numeroDocumento || "",
        luogoIntervento: luogoIntervento || "",
        meseCompetenza,
        settore,
        descrizione,
        unitaMisura,
        importo: importoNumber,
        createdBy: user?.uid || "",
        createdByName: user?.displayName || user?.email || "",
      });
      if (created === null) {
        setFormError("Impossibile salvare il ricavo.");
        return;
      }

      setFormState({
        cliente: "",
        nrFattura: "",
        numero_documento: "",
        luogoIntervento: "",
        meseCompetenza: "",
        settore: "",
        descrizione: "",
        unitaMisura: "",
        importo: "",
      });
      setShowAddModal(false);
    } finally {
      setIsSavingAdd(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError("");
    setIsSavingEdit(true);

    const id = editingRowId;
    if (!id) {
      setIsSavingEdit(false);
      return;
    }

    const cliente = editState.cliente.trim();
    const nrFattura = String(editState.nrFattura || "").trim();
    const numeroDocumento = String(editState.numero_documento || "").trim();
    const luogoIntervento = String(editState.luogoIntervento || "").trim();
    const meseCompetenza = String(editState.meseCompetenza || "").trim();
    const settore = editState.settore.trim();
    const descrizione = editState.descrizione.trim();
    const unitaMisura = editState.unitaMisura.trim();
    const importoNumber = parseImporto(editState.importo);

    if (
      !cliente ||
      !meseCompetenza ||
      !settore ||
      !descrizione ||
      !unitaMisura
    ) {
      setEditError("Compila tutti i campi.");
      setIsSavingEdit(false);
      return;
    }
    if (!Number.isFinite(importoNumber)) {
      setEditError("Importo non valido.");
      setIsSavingEdit(false);
      return;
    }

    try {
      const updated = await updateDocument(id, {
        cliente,
        nrFattura: nrFattura || "",
        numero_documento: numeroDocumento || "",
        luogoIntervento: luogoIntervento || "",
        meseCompetenza,
        settore,
        descrizione,
        unitaMisura,
        importo: importoNumber,
        updatedAt: timestamp.fromDate(new Date()),
        updatedBy: user?.uid || "",
        updatedByName: user?.displayName || user?.email || "",
      });

      if (updated === null) {
        setEditError("Impossibile salvare le modifiche.");
        return;
      }

      closeEditModal();
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row?.id) return;
    if (deletingId) return;
    const label = `${row?.cliente || ""} - ${row?.descrizione || ""}`.trim();
    const ok = window.confirm(
      `Eliminare questo ricavo?${label ? `\n\n${label}` : ""}`,
    );
    if (!ok) return;
    setDeletingId(row.id);
    try {
      await deleteDocument(row.id);
    } finally {
      setDeletingId("");
    }
  };

  const selectedConsuntivo = useMemo(() => {
    if (!selectedConsuntivoId) return null;
    return (Array.isArray(consuntivi) ? consuntivi : []).find(
      (c) => c.id === selectedConsuntivoId,
    );
  }, [consuntivi, selectedConsuntivoId]);

  useEffect(() => {
    const load = async () => {
      if (!showImportModal) return;
      if (!selectedConsuntivo) {
        setImportRows([]);
        return;
      }

      setImportIsLoading(true);
      setImportError("");
      try {
        const cliente = (selectedConsuntivo?.cliente ?? "").toString().trim();
        const baseSettore =
          (selectedConsuntivo?.settore?.codice ?? "").toString().trim() || "";
        const docIds = Array.isArray(selectedConsuntivo?.documenti_inclusi)
          ? selectedConsuntivo.documenti_inclusi
          : [];

        const docs = await Promise.all(
          docIds.map(async (id) => {
            try {
              const snap = await database
                .collection("bolle_vendita")
                .doc(id)
                .get();
              if (!snap.exists) return { id, missing: true };
              return { id: snap.id, ...snap.data() };
            } catch (_) {
              return { id, missing: true };
            }
          }),
        );

        const competenzaFromConsuntivo = getMonthKey(
          selectedConsuntivo?.data_creazione,
        );
        const docDates = docs
          .map((d) => toJsDate(d?.data_documento))
          .filter(Boolean)
          .sort((a, b) => a.getTime() - b.getTime());
        const competenzaFromDocs =
          docDates.length > 0 ? getMonthKey(docDates[0]) : "";
        const defaultCompetenza =
          competenzaFromDocs ||
          competenzaFromConsuntivo ||
          getMonthKey(new Date());
        setImportMeseCompetenza(defaultCompetenza);

        const materiali = Array.isArray(selectedConsuntivo?.materiali)
          ? selectedConsuntivo.materiali
          : [];
        const manodopera = Array.isArray(selectedConsuntivo?.manodopera)
          ? selectedConsuntivo.manodopera
          : [];
        const diritti = Array.isArray(selectedConsuntivo?.dirittiChiamata)
          ? selectedConsuntivo.dirittiChiamata
          : [];

        if (docs.length === 0) {
          const total = parseNumberIT(selectedConsuntivo?.totale_complessivo);
          const importo = Number.isFinite(total) ? total : 0;
          setImportRows([
            {
              key: selectedConsuntivo.id,
              docId: "",
              rif: "",
              numeroDocumento: "",
              cliente,
              descrizione: "Consuntivo",
              unitaMisura: "nr",
              importo,
              settore: baseSettore,
              luogoIntervento: (
                selectedConsuntivo?.luogo_lavoro ?? ""
              ).toString(),
            },
          ]);
          setUniformSector(baseSettore);
          return;
        }

        const rows = docs.map((doc) => {
          const docMateriali = materiali.filter((m) => m?.docId === doc.id);
          const docManodopera = manodopera.filter((m) => m?.docId === doc.id);
          const docDiritti = diritti.filter(
            (d) => d?.docId === doc.id && d?.applicato,
          );

          const totMat = docMateriali.reduce((acc, m) => {
            const q = parseNumberIT(m?.quantita);
            const pu = parseNumberIT(m?.prezzo_unitario);
            if (!Number.isFinite(q) || !Number.isFinite(pu)) return acc;
            return acc + q * pu;
          }, 0);

          const totMan = docManodopera.reduce((acc, m) => {
            const ore = parseNumberIT(m?.totale_ore);
            const tariffa = parseNumberIT(m?.tariffa_oraria);
            if (!Number.isFinite(ore) || !Number.isFinite(tariffa)) return acc;
            return acc + ore * tariffa;
          }, 0);

          const totDir = docDiritti.reduce((acc, d) => {
            const v = parseNumberIT(d?.valore);
            if (!Number.isFinite(v)) return acc;
            return acc + v;
          }, 0);

          const importo = totMat + totMan + totDir;
          const rif = (doc?.id ?? "").toString();
          const numeroDocumento = (doc?.numero_documento ?? "").toString();
          const descrizione = (doc?.oggetto_intervento ?? "").toString().trim();
          const luogoIntervento =
            (doc?.luogo_intervento ?? "").toString().trim() ||
            (selectedConsuntivo?.luogo_lavoro ?? "").toString().trim();

          return {
            key: doc.id,
            docId: doc.id,
            rif,
            numeroDocumento,
            cliente,
            descrizione: descrizione || "Intervento",
            unitaMisura: "nr",
            importo,
            settore: baseSettore,
            luogoIntervento,
          };
        });

        setImportRows(rows);
        setUniformSector(baseSettore);
      } catch (e) {
        setImportError("Impossibile caricare i dati del consuntivo.");
        setImportRows([]);
      } finally {
        setImportIsLoading(false);
      }
    };

    load();
  }, [selectedConsuntivo, showImportModal]);

  const applyUniformSettore = (settoreCode) => {
    setUniformSector(settoreCode);
    setImportRows((prev) =>
      prev.map((r) => ({ ...r, settore: settoreCode || "" })),
    );
  };

  const setRowSettore = (key, settoreCode) => {
    setImportRows((prev) =>
      prev.map((r) => (r.key === key ? { ...r, settore: settoreCode } : r)),
    );
  };

  const handleImport = async () => {
    setImportError("");
    const rows = Array.isArray(importRows) ? importRows : [];
    if (!selectedConsuntivoId) {
      setImportError("Seleziona un consuntivo.");
      return;
    }
    if (rows.length === 0) {
      setImportError("Nessuna riga da importare.");
      return;
    }
    const meseCompetenza = String(importMeseCompetenza || "").trim();
    if (!meseCompetenza) {
      setImportError("Seleziona il mese di competenza.");
      return;
    }
    const missingSettore = rows.some(
      (r) => !(r?.settore ?? "").toString().trim(),
    );
    if (missingSettore) {
      setImportError("Assegna un settore a tutte le righe prima di importare.");
      return;
    }
    const invalidImporto = rows.some((r) => !Number.isFinite(r?.importo));
    if (invalidImporto) {
      setImportError("Importo non valido in una o più righe.");
      return;
    }

    try {
      setIsSavingImport(true);
      const batch = database.batch();
      const createdAt = timestamp.fromDate(new Date());
      rows.forEach((r) => {
        const ref = database.collection("ricavi").doc();
        batch.set(ref, {
          cliente: r.cliente,
          nrFattura: "",
          meseCompetenza,
          settore: r.settore,
          descrizione: r.descrizione,
          unitaMisura: r.unitaMisura,
          importo: r.importo,
          numero_documento: (r?.numeroDocumento ?? "").toString(),
          luogoIntervento: (r?.luogoIntervento ?? "").toString(),
          createdAt,
          createdBy: user?.uid || "",
          createdByName: user?.displayName || user?.email || "",
        });
      });
      await batch.commit();
      setShowImportModal(false);
      setSelectedConsuntivoId("");
      setImportRows([]);
      alert(`Import completato: ${rows.length} righe create.`);
    } catch (e) {
      setImportError("Errore durante l'import.");
    } finally {
      setIsSavingImport(false);
    }
  };

  const toggleRowSelected = (id) => {
    setBulkError("");
    setSelectedRowIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAllVisible = () => {
    setBulkError("");
    if (!filteredRows || filteredRows.length === 0) return;
    if (allVisibleSelected) {
      setSelectedRowIds((prev) =>
        prev.filter((id) => !filteredRowIdSet.has(id)),
      );
    } else {
      setSelectedRowIds((prev) => {
        const out = new Set(prev);
        filteredRows.forEach((r) => out.add(r.id));
        return Array.from(out);
      });
    }
  };

  const handleBulkAssignInvoice = async () => {
    setBulkError("");
    const nr = bulkNrFattura.trim();
    if (!nr) {
      setBulkError("Inserisci il numero fattura.");
      return;
    }
    if (!selectedRowIds || selectedRowIds.length === 0) return;

    try {
      setIsAssigningInvoice(true);
      const batch = database.batch();
      const updatedAt = timestamp.fromDate(new Date());
      selectedRowIds.forEach((id) => {
        batch.update(database.collection("ricavi").doc(id), {
          nrFattura: nr,
          updatedAt,
          updatedBy: user?.uid || "",
          updatedByName: user?.displayName || user?.email || "",
        });
      });
      await batch.commit();
      setSelectedRowIds([]);
      setBulkNrFattura("");
    } catch (e) {
      setBulkError("Impossibile assegnare il numero fattura.");
    } finally {
      setIsAssigningInvoice(false);
    }
  };

  if (!admin) {
    return (
      <div
        className="gestione-documenti"
        style={{ width: "100%", maxWidth: "none" }}
      >
        <div className="gestione-header">
          <div>
            <h1>Ricavi (Fatturazione)</h1>
            <p>Accesso non autorizzato.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="gestione-documenti"
      style={{ width: "100%", maxWidth: "none" }}
    >
      <div className="gestione-header">
        <div>
          <h1>Ricavi (Fatturazione)</h1>
          <p>
            {filteredRows.length} righe (su{" "}
            {(Array.isArray(documents) ? documents.length : 0) || 0} totali)
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            type="button"
            className="gd-tab active"
            onClick={openNewModal}
          >
            Nuovo ricavo
          </button>
          <button type="button" className="gd-tab" onClick={openImportModal}>
            Import consuntivi
          </button>
        </div>
      </div>

      <div className="gd-filters">
        <div className="gd-filter-row">
          <div className="gd-filter-field gd-search">
            <label>Cerca</label>
            <input
              type="text"
              className="search-input"
              placeholder="Cliente, settore, descrizione, UM..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="gd-filter-field">
            <label>Mese competenza</label>
            <div className="gd-month">
              <input
                type="month"
                value={filterMeseCompetenza}
                onChange={(e) => setFilterMeseCompetenza(e.target.value)}
              />
              <button
                type="button"
                className="gd-clear-btn"
                onClick={() => setFilterMeseCompetenza("")}
                disabled={!filterMeseCompetenza}
                aria-label="Rimuovi filtro mese"
                title="Rimuovi filtro mese"
              >
                ×
              </button>
            </div>
          </div>
          <div className="gd-filter-field">
            <label>Totale ricavi</label>
            <div
              style={{
                height: 42,
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
                background: "#fff",
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                fontWeight: 700,
              }}
              title="Totale importi delle righe filtrate"
            >
              {formatCurrency(filteredTotalImporto)}
            </div>
          </div>
        </div>

        <div className="gd-filter-row">
          <div className="gd-filter-field">
            <label>&nbsp;</label>
            <button
              type="button"
              className={`gd-chip ${onlyNoInvoice ? "active" : ""}`}
              onClick={() => setOnlyNoInvoice((v) => !v)}
            >
              Senza nr fattura
            </button>
          </div>
          <div className="gd-filter-field">
            <label>&nbsp;</label>
            <button
              type="button"
              className="gd-tab"
              onClick={() => {
                setMonthDetailMonth(String(filterMeseCompetenza || "").trim());
                setShowMonthDetailModal(true);
              }}
              title="Apri dettaglio del mese"
            >
              Dettaglio mese
            </button>
          </div>
          <div className="gd-filter-field">
            <label>&nbsp;</label>
            <button
              type="button"
              className="gd-tab"
              onClick={() => {
                setDownloadMonth(String(filterMeseCompetenza || "").trim());
                setShowDownloadModal(true);
              }}
              title="Scarica CSV del mese"
            >
              Download dati
            </button>
          </div>
        </div>
        {error && <div className="error">{error}</div>}
      </div>

      {fattureRaggruppate.length > 0 && (
        <div className="project-table-wrapper" style={{ marginBottom: 16 }}>
          <table className="project-table">
            <thead>
              <tr
                onClick={() => setShowFattureTable((p) => !p)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setShowFattureTable((p) => !p);
                  }
                }}
                tabIndex={0}
                role="button"
                title={
                  showFattureTable
                    ? "Nascondi tabella fatture"
                    : "Mostra tabella fatture"
                }
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                <th>{showFattureTable ? "▼" : "▶"} Nr fattura</th>
                <th>Cliente</th>
                <th>Righe</th>
                <th style={{ textAlign: "right" }}>Totale</th>
              </tr>
            </thead>
            {showFattureTable && (
              <tbody>
                {fattureRaggruppate.map((f) => (
                  <tr
                    key={f.nrFattura}
                    onClick={() => openInvoiceModal(f.nrFattura)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openInvoiceModal(f.nrFattura);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    style={{ cursor: "pointer" }}
                    title="Apri dettaglio voci"
                  >
                    <td>{f.nrFattura}</td>
                    <td>{f.cliente || "—"}</td>
                    <td>{f.count}</td>
                    <td style={{ textAlign: "right" }}>
                      {formatCurrency(f.totale)}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      )}

      {selectedRowIds.length > 0 && (
        <div className="gd-filters">
          <div
            className="gd-filter-row"
            style={{ display: "flex", gap: 16, flexWrap: "wrap" }}
          >
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <div className="gd-filter-field">
                <label>Nr fattura</label>
                <input
                  type="text"
                  className="search-input"
                  value={bulkNrFattura}
                  onChange={(e) => setBulkNrFattura(e.target.value)}
                  placeholder="Es. 12/2026"
                />
              </div>
              <div className="gd-filter-field">
                <label>&nbsp;</label>
                <button
                  type="button"
                  className="gd-tab active"
                  onClick={handleBulkAssignInvoice}
                  disabled={isAssigningInvoice}
                >
                  {isAssigningInvoice ? "Salvataggio..." : "Conferma"}
                </button>
              </div>
              <div className="gd-filter-field">
                <label>&nbsp;</label>
                <div
                  style={{ display: "flex", alignItems: "center", height: 42 }}
                >
                  {selectedRowIds.length} selezionate
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "flex-end",
                paddingLeft: 16,
                borderLeft: "1px solid #e0e0e0",
              }}
            >
              <div className="gd-filter-field">
                <label>Duplica</label>
                <button
                  type="button"
                  className="gd-tab"
                  onClick={openDuplicateModal}
                  disabled={!selectedRowForDuplication}
                  title={
                    selectedRowForDuplication
                      ? "Duplica il ricavo selezionato"
                      : "Seleziona una sola riga per duplicare"
                  }
                >
                  Duplica
                </button>
              </div>
            </div>
          </div>
          {bulkError && <div className="error">{bulkError}</div>}
        </div>
      )}

      <div className="project-table-wrapper">
        <table className="project-table">
          <thead>
            <tr>
              <th style={{ width: 46, textAlign: "center" }}>
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  aria-label="Seleziona tutte le righe visibili"
                />
              </th>
              <th>Cliente</th>
              <th>Nr fattura</th>
              <th>Riferimento</th>
              <th>Luogo intervento</th>
              <th>Settore</th>
              <th>Descrizione</th>
              <th>UM</th>
              <th>Importo</th>
              <th style={{ width: 110, textAlign: "right" }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ padding: 16, color: "#666" }}>
                  Nessun ricavo trovato.
                </td>
              </tr>
            ) : (
              filteredRows.map((row) => (
                <tr key={row.id}>
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={selectedRowIds.includes(row.id)}
                      onChange={() => toggleRowSelected(row.id)}
                      aria-label={`Seleziona riga ${row.id}`}
                    />
                  </td>
                  <td>{row?.cliente || "—"}</td>
                  <td>{row?.nrFattura || "—"}</td>
                  <td>{row?.numero_documento || "—"}</td>
                  <td>{row?.luogoIntervento || "—"}</td>
                  <td>{row?.settore || "—"}</td>
                  <td>{row?.descrizione || "—"}</td>
                  <td>{row?.unitaMisura || "—"}</td>
                  <td>{formatCurrency(row?.importo)}</td>
                  <td>
                    <div className="actions">
                      <button
                        type="button"
                        className="btn-icon edit"
                        onClick={() => openEditModal(row)}
                        aria-label="Modifica"
                        title="Modifica"
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="btn-icon delete"
                        onClick={() => handleDelete(row)}
                        aria-label="Elimina"
                        title="Elimina"
                        disabled={deletingId === row.id}
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {duplicateSourceId ? "Duplica ricavo" : "Nuovo ricavo"}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeModal}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {formError && <div className="error">{formError}</div>}

                <div className="form-group">
                  <label>Cliente</label>
                  <input
                    type="text"
                    value={formState.cliente}
                    onChange={(e) =>
                      setFormState((p) => ({ ...p, cliente: e.target.value }))
                    }
                    placeholder="Es. Rossi SRL"
                  />
                </div>

                <div className="form-group">
                  <label>Nr fattura</label>
                  <input
                    type="text"
                    value={formState.nrFattura}
                    onChange={(e) =>
                      setFormState((p) => ({ ...p, nrFattura: e.target.value }))
                    }
                    placeholder="Es. 12/2026"
                  />
                </div>

                <div className="form-group">
                  <label>Riferimento</label>
                  <input
                    type="text"
                    value={formState.numero_documento}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        numero_documento: e.target.value,
                      }))
                    }
                    placeholder="Es. DDT 123"
                  />
                </div>

                <div className="form-group">
                  <label>Luogo intervento</label>
                  <input
                    type="text"
                    value={formState.luogoIntervento}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        luogoIntervento: e.target.value,
                      }))
                    }
                    placeholder="Es. Via Roma 10, Milano"
                  />
                </div>

                <div className="form-group">
                  <label>Mese di competenza</label>
                  <input
                    type="month"
                    value={formState.meseCompetenza}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        meseCompetenza: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Settore</label>
                  <Select
                    classNamePrefix="react-select"
                    styles={selectModalStyles}
                    menuPortalTarget={document.body}
                    options={projectOptions}
                    value={
                      projectOptions.find(
                        (o) => o.value === (formState.settore ?? ""),
                      ) || null
                    }
                    onChange={(opt) =>
                      setFormState((p) => ({
                        ...p,
                        settore: opt ? opt.value : "",
                      }))
                    }
                    isClearable={true}
                    isSearchable={true}
                    placeholder="Seleziona progetto/settore..."
                    noOptionsMessage={() => "Nessun progetto trovato"}
                  />
                </div>

                <div className="form-group">
                  <label>Descrizione</label>
                  <input
                    type="text"
                    value={formState.descrizione}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        descrizione: e.target.value,
                      }))
                    }
                    placeholder="Es. Installazione quadro elettrico"
                  />
                </div>

                <div className="form-group">
                  <label>Unità di misura</label>
                  <input
                    type="text"
                    value={formState.unitaMisura}
                    onChange={(e) =>
                      setFormState((p) => ({
                        ...p,
                        unitaMisura: e.target.value,
                      }))
                    }
                    placeholder="Es. ore / pz / m²"
                  />
                </div>

                <div className="form-group">
                  <label>Importo</label>
                  <input
                    type="text"
                    value={formState.importo}
                    onChange={(e) =>
                      setFormState((p) => ({ ...p, importo: e.target.value }))
                    }
                    placeholder="Es. 1200,00"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <div
                  className="gd-form-actions"
                  style={{ margin: 0, padding: 0, borderTop: "none" }}
                >
                  <button
                    type="button"
                    className="gd-btn-cancel"
                    onClick={closeModal}
                    disabled={isSavingAdd}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="gd-btn-save"
                    disabled={isSavingAdd}
                  >
                    {isSavingAdd ? "Salvataggio..." : "Salva"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Modifica ricavo</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeEditModal}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                {editError && <div className="error">{editError}</div>}

                <div className="form-group">
                  <label>Cliente</label>
                  <input
                    type="text"
                    value={editState.cliente}
                    onChange={(e) =>
                      setEditState((p) => ({ ...p, cliente: e.target.value }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Nr fattura</label>
                  <input
                    type="text"
                    value={editState.nrFattura}
                    onChange={(e) =>
                      setEditState((p) => ({ ...p, nrFattura: e.target.value }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Riferimento</label>
                  <input
                    type="text"
                    value={editState.numero_documento}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        numero_documento: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Luogo intervento</label>
                  <input
                    type="text"
                    value={editState.luogoIntervento}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        luogoIntervento: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Mese di competenza</label>
                  <input
                    type="month"
                    value={editState.meseCompetenza}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        meseCompetenza: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Settore</label>
                  <Select
                    classNamePrefix="react-select"
                    styles={selectModalStyles}
                    menuPortalTarget={document.body}
                    options={projectOptions}
                    value={
                      projectOptions.find(
                        (o) => o.value === (editState.settore ?? ""),
                      ) || null
                    }
                    onChange={(opt) =>
                      setEditState((p) => ({
                        ...p,
                        settore: opt ? opt.value : "",
                      }))
                    }
                    isClearable={true}
                    isSearchable={true}
                    placeholder="Seleziona progetto/settore..."
                    noOptionsMessage={() => "Nessun progetto trovato"}
                  />
                </div>

                <div className="form-group">
                  <label>Descrizione</label>
                  <input
                    type="text"
                    value={editState.descrizione}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        descrizione: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Unità di misura</label>
                  <input
                    type="text"
                    value={editState.unitaMisura}
                    onChange={(e) =>
                      setEditState((p) => ({
                        ...p,
                        unitaMisura: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Importo</label>
                  <input
                    type="text"
                    value={editState.importo}
                    onChange={(e) =>
                      setEditState((p) => ({ ...p, importo: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="modal-footer">
                <div
                  className="gd-form-actions"
                  style={{ margin: 0, padding: 0, borderTop: "none" }}
                >
                  <button
                    type="button"
                    className="gd-btn-cancel"
                    onClick={closeEditModal}
                    disabled={isSavingEdit}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="gd-btn-save"
                    disabled={isSavingEdit}
                  >
                    {isSavingEdit ? "Salvataggio..." : "Salva modifiche"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="modal-overlay" onClick={closeImportModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 1100 }}
          >
            <div className="modal-header">
              <h3 className="modal-title">Import consuntivi</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeImportModal}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {importError && <div className="error">{importError}</div>}

              <div className="form-group">
                <label>Mese competenza</label>
                <input
                  type="month"
                  value={consuntiviMonthFilter}
                  onChange={(e) => {
                    setConsuntiviMonthFilter(e.target.value);
                    setSelectedConsuntivoId("");
                    setImportRows([]);
                    setImportError("");
                  }}
                />
              </div>

              <div className="form-group">
                <label>Consuntivo</label>
                <Select
                  classNamePrefix="react-select"
                  styles={selectModalStyles}
                  menuPortalTarget={document.body}
                  options={consuntivoOptions}
                  value={
                    consuntivoOptions.find(
                      (o) => o.value === selectedConsuntivoId,
                    ) || null
                  }
                  onChange={(opt) => {
                    setImportError("");
                    setSelectedConsuntivoId(opt ? opt.value : "");
                  }}
                  isClearable={true}
                  isSearchable={true}
                  placeholder="Seleziona un consuntivo..."
                  noOptionsMessage={() => "Nessun consuntivo trovato"}
                />
              </div>

              {selectedConsuntivoId && (
                <>
                  <div className="form-group">
                    <label>Mese di competenza</label>
                    <input
                      type="month"
                      value={importMeseCompetenza}
                      onChange={(e) => setImportMeseCompetenza(e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label
                      style={{ display: "flex", gap: 10, alignItems: "center" }}
                    >
                      <input
                        type="checkbox"
                        checked={useUniformSector}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseUniformSector(checked);
                          if (checked) {
                            applyUniformSettore(uniformSector);
                          }
                        }}
                      />
                      Settore univoco per tutti gli interventi
                    </label>
                  </div>

                  {useUniformSector && (
                    <div className="form-group">
                      <label>Settore</label>
                      <Select
                        classNamePrefix="react-select"
                        styles={selectModalStyles}
                        menuPortalTarget={document.body}
                        options={projectOptions}
                        value={
                          projectOptions.find(
                            (o) => o.value === uniformSector,
                          ) || null
                        }
                        onChange={(opt) =>
                          applyUniformSettore(opt ? opt.value : "")
                        }
                        isClearable={true}
                        isSearchable={true}
                        placeholder="Seleziona settore..."
                        noOptionsMessage={() => "Nessun progetto trovato"}
                      />
                    </div>
                  )}

                  {importIsLoading ? (
                    <div style={{ color: "#666" }}>
                      Caricamento interventi...
                    </div>
                  ) : (
                    <div className="project-table-wrapper">
                      <table className="project-table">
                        <thead>
                          <tr>
                            <th>Rif</th>
                            <th>Numero documento</th>
                            <th>Descrizione</th>
                            <th>Importo</th>
                            <th>Settore</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importRows.length === 0 ? (
                            <tr>
                              <td
                                colSpan={5}
                                style={{ padding: 16, color: "#666" }}
                              >
                                Nessuna riga da importare.
                              </td>
                            </tr>
                          ) : (
                            importRows.map((r) => (
                              <tr key={r.key}>
                                <td>{r.rif || "—"}</td>
                                <td>{r.numeroDocumento || "—"}</td>
                                <td>{r.descrizione || "—"}</td>
                                <td>{formatCurrency(r.importo)}</td>
                                <td style={{ minWidth: 280 }}>
                                  {useUniformSector ? (
                                    projectOptions.find(
                                      (o) => o.value === r.settore,
                                    )?.label || "—"
                                  ) : (
                                    <Select
                                      classNamePrefix="react-select"
                                      styles={selectModalStyles}
                                      menuPortalTarget={document.body}
                                      options={projectOptions}
                                      value={
                                        projectOptions.find(
                                          (o) => o.value === (r.settore ?? ""),
                                        ) || null
                                      }
                                      onChange={(opt) =>
                                        setRowSettore(
                                          r.key,
                                          opt ? opt.value : "",
                                        )
                                      }
                                      isClearable={true}
                                      isSearchable={true}
                                      placeholder="Settore..."
                                      noOptionsMessage={() =>
                                        "Nessun progetto trovato"
                                      }
                                    />
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer">
              <div
                className="gd-form-actions"
                style={{ margin: 0, padding: 0, borderTop: "none" }}
              >
                <button
                  type="button"
                  className="gd-btn-cancel"
                  onClick={closeImportModal}
                  disabled={importIsLoading || isSavingImport}
                >
                  Annulla
                </button>
                <button
                  type="button"
                  className="gd-btn-save"
                  onClick={handleImport}
                  disabled={
                    importIsLoading ||
                    isSavingImport ||
                    !selectedConsuntivoId ||
                    importRows.length === 0 ||
                    !importMeseCompetenza
                  }
                >
                  {isSavingImport ? "Importazione..." : "Importa"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showInvoiceModal && (
        <div className="modal-overlay" onClick={closeInvoiceModal}>
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 1100 }}
          >
            <div className="modal-header">
              <h3 className="modal-title">
                Dettaglio fattura {selectedInvoiceNr}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={closeInvoiceModal}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div style={{ color: "#666", marginBottom: 12 }}>
                {invoiceDetailRows.length} righe — Totale{" "}
                {formatCurrency(invoiceDetailTotal)}
              </div>

              <div className="project-table-wrapper">
                <table className="project-table">
                  <thead>
                    <tr>
                      <th>Cliente</th>
                      <th>Riferimento</th>
                      <th>Luogo intervento</th>
                      <th>Settore</th>
                      <th>Descrizione</th>
                      <th>UM</th>
                      <th style={{ textAlign: "right" }}>Importo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceDetailRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{ padding: 16, color: "#666" }}>
                          Nessuna riga trovata.
                        </td>
                      </tr>
                    ) : (
                      invoiceDetailRows.map((r) => (
                        <tr key={r.id}>
                          <td>{r?.cliente || "—"}</td>
                          <td>{r?.numero_documento || "—"}</td>
                          <td>{r?.luogoIntervento || "—"}</td>
                          <td>
                            {projectOptions.find((o) => o.value === r?.settore)
                              ?.label ||
                              r?.settore ||
                              "—"}
                          </td>
                          <td>{r?.descrizione || "—"}</td>
                          <td>{r?.unitaMisura || "—"}</td>
                          <td style={{ textAlign: "right" }}>
                            {formatCurrency(r?.importo)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMonthDetailModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowMonthDetailModal(false);
            setMonthDetailMonth("");
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 1100 }}
          >
            <div className="modal-header">
              <h3 className="modal-title">
                Dettaglio mese {monthDetailMonth || "—"}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => {
                  setShowMonthDetailModal(false);
                  setMonthDetailMonth("");
                }}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {!monthDetailMonth ? (
                <div className="form-group">
                  <label>Mese da analizzare</label>
                  <input
                    type="month"
                    value={monthDetailMonth}
                    onChange={(e) => setMonthDetailMonth(e.target.value)}
                  />
                </div>
              ) : macroSettoreTotals.length === 0 ? (
                <div style={{ padding: 16, color: "#666" }}>
                  Nessun ricavo per il mese selezionato.
                </div>
              ) : (
                <div className="project-table-wrapper">
                  <table className="project-table">
                    <thead>
                      <tr>
                        <th>Macro settore</th>
                        <th>Righe</th>
                        <th style={{ textAlign: "right" }}>Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {macroSettoreTotals.map((r) => (
                        <tr key={r.macro}>
                          <td>{r.macro}</td>
                          <td>{r.count}</td>
                          <td style={{ textAlign: "right" }}>
                            {formatCurrency(r.totale)}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td style={{ fontWeight: 600 }}>Totale</td>
                        <td style={{ fontWeight: 600 }}>
                          {macroSettoreTotals.reduce(
                            (acc, r) => acc + r.count,
                            0,
                          )}
                        </td>
                        <td style={{ textAlign: "right", fontWeight: 600 }}>
                          {formatCurrency(macroSettoreGrandTotal)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showDownloadModal && (
        <div
          className="modal-overlay"
          onClick={() => {
            setShowDownloadModal(false);
            setDownloadMonth("");
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 800 }}
          >
            <div className="modal-header">
              <h3 className="modal-title">
                Download dati{" "}
                {selectedRowIds.length > 0
                  ? " (selezione)"
                  : downloadMonth || "—"}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => {
                  setShowDownloadModal(false);
                  setDownloadMonth("");
                }}
                aria-label="Chiudi"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              {selectedRowIds.length > 0 ? (
                <div style={{ color: "#666", marginBottom: 12 }}>
                  Esporterai {selectedRowIds.length} righe selezionate
                </div>
              ) : (
                <>
                  <div className="form-group">
                    <label>Mese di competenza</label>
                    <input
                      type="month"
                      value={downloadMonth}
                      onChange={(e) => setDownloadMonth(e.target.value)}
                    />
                  </div>

                  {downloadMonth ? (
                    <div style={{ color: "#666", marginBottom: 12 }}>
                      {downloadMonthRows.length} righe trovate
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="modal-footer">
              <div
                className="gd-form-actions"
                style={{ margin: 0, padding: 0, borderTop: "none" }}
              >
                <button
                  type="button"
                  className="gd-btn-cancel"
                  onClick={() => {
                    setShowDownloadModal(false);
                    setDownloadMonth("");
                  }}
                >
                  Chiudi
                </button>
                <button
                  type="button"
                  className="gd-btn-save"
                  onClick={handleDownloadCsv}
                  disabled={selectedRowIds.length > 0 ? false : !downloadMonth}
                >
                  Scarica CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
